import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  InputGroup,
  InputLeftElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Text
} from '@chakra-ui/react'
import type { InsurerOrganizationUnitDiscount } from 'BrokerTsLib/src/dtos'
import type { InsuranceCategoryDiscount } from 'BrokerTsLib/src/dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import type {
  FieldPath,
  FieldValues
} from 'BrokerTsLib/src/rhf'
import {
  useController,
  useFormContext
} from 'BrokerTsLib/src/rhf'
import { memo, useMemo } from 'react'

export const DiscountEditForm = ({
  unit,
  insurerMinDiscount,
  insurerMaxDiscount,
  conflictDiscounts,
}: {
  unit: string
  insurerMinDiscount?: number
  insurerMaxDiscount?: number
  conflictDiscounts: InsuranceCategoryDiscount[]
}) => {
  const {
    register,
    formState: { isDirty, isSubmitting },
    watch,
  } = useFormContext<InsurerOrganizationUnitDiscount>()

  const maxDiscountValue = watch('maxDiscount')
  const maxHiddenDiscountValue = (insurerMaxDiscount ?? 0) - maxDiscountValue

  return (
    <Grid
      sx={{
        gap: 6,
        justifyItems: 'start',
      }}
    >
      <Flex
        sx={{
          gap: 6,
          placeItems: 'start',
          w: '100%',
          flexDir: { base: 'column', xs: 'row' },
        }}
      >
        <DiscountNumberInput
          name="maxDiscount"
          label="Maximální"
          unit={unit}
          max={insurerMaxDiscount ?? 100}
          min={insurerMinDiscount ?? 0}
        />

        <DiscountNumberInput
          name="defaultDiscount"
          label="Výchozí"
          helperText={`Min: ${insurerMinDiscount} ${unit}, Max: ${insurerMaxDiscount} ${unit}`}
          unit={unit}
          max={maxDiscountValue}
          min={insurerMinDiscount ?? 0}
        />

        <DiscountNumberInput
          name="hiddenDiscount"
          label="Skrytá"
          unit={unit}
          max={maxHiddenDiscountValue}
          min={0}
        />

        <FormControl>
          <FormLabel>Povolená</FormLabel>

          <InputGroup mt={{ base: 0, sm: 2 }}>
            <Switch size="lg" {...register('valid')}></Switch>
          </InputGroup>
        </FormControl>
      </Flex>
      <Flex
        sx={{
          gap: 4,
          alignItems: 'baseline',
          flexWrap: 'wrap',
          w: '100%',
          justifyContent: 'space-between',
          mb: 4,
        }}
      >
        <Button
          colorScheme="primary"
          type="submit"
          isDisabled={!isDirty}
          isLoading={isSubmitting}
        >
          Uložit slevu
        </Button>
        {(conflictDiscounts?.length ?? 0) > 0 && (
          <ConflictDiscounts conflicts={conflictDiscounts} />
        )}
      </Flex>
    </Grid>
  )
}

const ConflictDiscounts = ({
  conflicts,
}: {
  conflicts: InsuranceCategoryDiscount[]
}) => {
  return (
    <Flex fontSize="sm" whiteSpace="pre-wrap" flexFlow="wrap">
      <Text>Nelze kombinovat s:</Text>
      {conflicts.map((conflict, index) => (
        <Text key={index}>
          {` ${conflict.name}`}
          {index < conflicts.length - 1 && ','}
        </Text>
      ))}
    </Flex>
  )
}

const DiscountNumberInput = memo(function <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  helperText,
  unit,
  max,
  min,
}: {
  name: TName
  label: string
  helperText?: string
  unit: string
  max: number | undefined
  min: number | undefined
}) {
  const minActual = useMemo(() => min ?? 0, [min])
  const maxActual = useMemo(() => max ?? 100, [max])

  const { control } = useFormContext<TFieldValues>()

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: { min: minActual, max: maxActual },
  })

  return (
    <FormControl isRequired isInvalid={!!error}>
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        <InputLeftElement pointerEvents="none" color="black" fontSize="md">
          {unit}
        </InputLeftElement>
        <NumberInput
          w="120px"
          value={field.value}
          onChange={(_, valueAsNumber) =>
            field.onChange(Number.isNaN(valueAsNumber) ? '' : valueAsNumber)
          }
          onBlur={field.onBlur}
          min={minActual}
          max={maxActual}
          keepWithinRange={false}
          clampValueOnBlur={false}
        >
          <NumberInputField pl={8} ref={field.ref} name={field.name} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </InputGroup>

      {helperText && (
        <FormHelperText
          sx={{
            mt: -1.5,
            width: '100%',
            maxW: '120px',
            textAlign: 'center',
          }}
        >
          {helperText}
        </FormHelperText>
      )}
      <FormErrorMessage>{error?.message?.toString()}</FormErrorMessage>
    </FormControl>
  )
})
